import React, {useState} from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
} from '@mui/material';
import {useGetShareableLinks} from '../../../hooks/productions/useProductions';
import {
  GetShareableLinksRequestParams,
  GetShareableLinksRequestBody,
} from '../../../api/types/production.type';
import '../Demo.scss';

const ShareableLinksApiTest: React.FC = () => {
  // Query Parameters
  const [productionId, setProductionId] = useState<number>(199);
  const [expiryDate, setExpiryDate] = useState<string>(
    '2025-11-25 08:18:09.347'
  );
  const [isRegenerate, setIsRegenerate] = useState<boolean>(true);

  // Request Body - Basic Info
  const [bodyProductionId, setBodyProductionId] = useState<number>(59);
  const [moduleType, setModuleType] = useState<string>('PRODUCTION');
  const [isMarkComplete, setIsMarkComplete] = useState<boolean>(false);
  const [shouldContinue, setShouldContinue] = useState<boolean>(false);
  const [canGenerate, setCanGenerate] = useState<boolean>(false);
  const [canRegenerate, setCanRegenerate] = useState<boolean>(true);
  const [securityGroupIdForProduction, setSecurityGroupIdForProduction] =
    useState<number>(1);
  const [productionType, setProductionType] = useState<number>(1);
  const [productionStateCode, setProductionStateCode] =
    useState<string>('AFTERDATAGENFAILED');
  const [productionTempararyFolderPath, setProductionTempararyFolderPath] =
    useState<string>(
      '\\\\devnas10.consiliotest.com\\AURORA_REVIEW_DEV4\\destinationlocation\\dndproject\\CADE5C9F-B105-4A5A-ACA2-AFBD3096E55E\\Temp\\Production59'
    );
  const [productionDestinationFolderPath, setProductionDestinationFolderPath] =
    useState<string>(
      '\\\\devnas10.consiliotest.com\\AURORA_REVIEW_DEV4\\Productions\\dndproject3\\pprod'
    );
  const [productionFolderPath, setProductionFolderPath] = useState<string>(
    '\\\\devnas10.consiliotest.com\\AURORA_REVIEW_DEV4\\destinationlocation\\dndproject\\CADE5C9F-B105-4A5A-ACA2-AFBD3096E55E\\57377E09-5AA8-48EC-97D2-3652B8D7B34D'
  );
  const [shouldPerformBlankPageDetection, setShouldPerformBlankPageDetection] =
    useState<boolean>(false);
  const [regenerateType, setRegenerateType] = useState<number>(0);

  const [enabled, setEnabled] = useState<boolean>(false);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const requestParams: GetShareableLinksRequestParams = {
    productionId: productionId,
    expiryDate: expiryDate,
    isRegenerate: isRegenerate,
  };

  const requestBody: GetShareableLinksRequestBody = {
    ProductionId: bodyProductionId,
    ModuleType: moduleType,
    IsMarkComplete: isMarkComplete,
    ShouldContinue: shouldContinue,
    CanGenerate: canGenerate,
    CanRegenerate: canRegenerate,
    SecurityGroupIdForProduction: securityGroupIdForProduction,
    ProductionType: productionType,
    ProductionStateCode: productionStateCode,
    ProductionTempararyFolderPath: productionTempararyFolderPath,
    ProductionDestinationFolderPath: productionDestinationFolderPath,
    ProductionFolderPath: productionFolderPath,
    ShouldPerformBlankPageDetection: shouldPerformBlankPageDetection,
    RegenerateType: regenerateType,
  };

  const {
    data: linksData,
    isLoading,
    error,
    refetch,
  } = useGetShareableLinks(requestParams, requestBody, enabled);

  const handleTestApi = () => {
    setEnabled(true);
  };

  const handleReset = () => {
    setEnabled(false);
  };

  const handleCopyJson = async () => {
    if (linksData) {
      try {
        await navigator.clipboard.writeText(JSON.stringify(linksData, null, 2));
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const handleCopyLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <Box className="demo-page" sx={{padding: 3}}>
      <Box sx={{marginBottom: 4}}>
        <Typography
          variant="h4"
          className="typo-text headline1_size"
          gutterBottom
        >
          🧪 Shareable Links API Test
        </Typography>
        <Typography className="typo-text body2_size" sx={{color: '#666'}}>
          Get shareable links for a production. Returns URLs for all files and
          DAT file, along with link password and expiry date.
        </Typography>
      </Box>

      {/* API Info */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              marginBottom: 2,
            }}
          >
            <Chip label="GET" color="primary" size="small" data-testid={`sharable-link-chip-info`} />
            <Typography variant="h6" className="typo-text subhead2_size">
              Get Shareable Links
            </Typography>
          </Box>
          <Typography
            className="typo-text body3_size"
            sx={{
              fontFamily: 'monospace',
              backgroundColor: '#f5f5f5',
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
              marginBottom: 1,
              fontSize: '12px',
            }}
          >
            /productions/api/v1/production/shareable-links
          </Typography>
          <Typography className="typo-text body3_size" sx={{color: '#666'}}>
            Get shareable links for a production. GET request with query
            parameters (productionId, expiryDate, isRegenerate) and request body
            containing production details. Returns URLs and password.
          </Typography>
        </CardContent>
      </Card>

      {/* Request Parameters */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Typography
            variant="h6"
            className="typo-text subhead3_size"
            gutterBottom
          >
            Query Parameters
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 2,
              marginBottom: 3,
            }}
          >
            <TextField
              label="Production ID"
              type="number"
              value={productionId}
              onChange={(e) => setProductionId(Number(e.target.value))}
              size="small"
            />
            <TextField
              label="Expiry Date"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              size="small"
              placeholder="e.g., 2025-11-25 08:18:09.347"
              helperText="Format: YYYY-MM-DD HH:mm:ss.SSS"
            />
            <FormControlLabel
            data-testid="is-regenerate-form-control-label"
              control={
                <Checkbox
                  data-testid="shareable-links-api-test-is-regenerate"
                  checked={isRegenerate}
                  onChange={(e) => setIsRegenerate(e.target.checked)}
                />
              }
              label="Is Regenerate"
            />
          </Box>

          <Typography
            variant="h6"
            className="typo-text subhead3_size"
            gutterBottom
            sx={{marginTop: 2}}
          >
            Request Body
          </Typography>

          {/* Basic Information */}
          <Box sx={{marginBottom: 3}}>
            <Typography
              className="typo-text subhead3_size"
              sx={{marginBottom: 2}}
            >
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Production ID"
                  type="number"
                  value={bodyProductionId}
                  onChange={(e) => setBodyProductionId(Number(e.target.value))}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Module Type"
                  value={moduleType}
                  onChange={(e) => setModuleType(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Security Group ID For Production"
                  type="number"
                  value={securityGroupIdForProduction}
                  onChange={(e) =>
                    setSecurityGroupIdForProduction(Number(e.target.value))
                  }
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Production Type"
                  type="number"
                  value={productionType}
                  onChange={(e) => setProductionType(Number(e.target.value))}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Production State Code"
                  value={productionStateCode}
                  onChange={(e) => setProductionStateCode(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Regenerate Type"
                  type="number"
                  value={regenerateType}
                  onChange={(e) => setRegenerateType(Number(e.target.value))}
                  size="small"
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>

          {/* Flags */}
          <Box sx={{marginBottom: 3}}>
            <Typography
              className="typo-text subhead3_size"
              sx={{marginBottom: 2}}
            >
              Flags
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                data-testid="is-mark-complete-form-control-label"
                  control={
                    <Checkbox
                      data-testid="shareable-links-api-test-is-mark-complete"
                      checked={isMarkComplete}
                      onChange={(e) => setIsMarkComplete(e.target.checked)}
                    />
                  }
                  label="Is Mark Complete"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                data-testid="should-continue-form-control-label"
                  control={
                    <Checkbox
                      data-testid="shareable-links-api-test-should-continue"
                      checked={shouldContinue}
                      onChange={(e) => setShouldContinue(e.target.checked)}
                    />
                  }
                  label="Should Continue"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                data-testid="can-generate-form-control-label"
                  control={
                    <Checkbox
                      data-testid="shareable-links-api-test-can-generate"
                      checked={canGenerate}
                      onChange={(e) => setCanGenerate(e.target.checked)}
                    />
                  }
                  label="Can Generate"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                data-testid="can-regenerate-form-control-label"
                  control={
                    <Checkbox
                      data-testid="shareable-links-api-test-can-regenerate"
                      checked={canRegenerate}
                      onChange={(e) => setCanRegenerate(e.target.checked)}
                    />
                  }
                  label="Can Regenerate"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                data-testid="should-perform-blank-page-detection-form-control-label"
                  control={
                    <Checkbox
                      data-testid="shareable-links-api-test-should-perform-blank-page-detection"
                      checked={shouldPerformBlankPageDetection}
                      onChange={(e) =>
                        setShouldPerformBlankPageDetection(e.target.checked)
                      }
                    />
                  }
                  label="Should Perform Blank Page Detection"
                />
              </Grid>
            </Grid>
          </Box>

          {/* Folder Paths */}
          <Box sx={{marginBottom: 3}}>
            <Typography
              className="typo-text subhead3_size"
              sx={{marginBottom: 2}}
            >
              Folder Paths
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Production Temporary Folder Path"
                  value={productionTempararyFolderPath}
                  onChange={(e) =>
                    setProductionTempararyFolderPath(e.target.value)
                  }
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Production Destination Folder Path"
                  value={productionDestinationFolderPath}
                  onChange={(e) =>
                    setProductionDestinationFolderPath(e.target.value)
                  }
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Production Folder Path"
                  value={productionFolderPath}
                  onChange={(e) => setProductionFolderPath(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>

          <Box sx={{display: 'flex', gap: 2}}>
            <Button variant="contained" color="primary" onClick={handleTestApi}>
              🚀 Test API
            </Button>
            <Button variant="outlined" onClick={handleReset}>
              🔄 Reset
            </Button>
            {enabled && (
              <Button variant="outlined" onClick={() => refetch()}>
                ♻️ Refetch
              </Button>
            )}
            {linksData && (
              <Button variant="outlined" onClick={handleCopyJson}>
                {copySuccess ? '✅ Copied!' : '📋 Copy JSON'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Loading State */}
      {isLoading && (
        <Card sx={{marginBottom: 3}}>
          <CardContent>
            <Alert severity="info">
              ⏳ Loading... Fetching shareable links...
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {error && (
        <Card sx={{marginBottom: 3}}>
          <CardContent>
            <Alert severity="error">
              <Typography className="typo-text subhead3_size" gutterBottom>
                ❌ Error
              </Typography>
              <Typography className="typo-text body3_size">
                {error.message ||
                  'An error occurred while fetching shareable links'}
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Response Data */}
      {linksData && !isLoading && (
        <>
          <Card sx={{marginBottom: 3}}>
            <CardContent>
              <Typography
                variant="h6"
                className="typo-text subhead3_size"
                gutterBottom
              >
                Shareable Links
              </Typography>

              {/* Links */}
              <Box sx={{marginBottom: 3}}>
                <Paper sx={{padding: 2, backgroundColor: '#f9f9f9'}}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Typography className="typo-text body3_size">
                          <strong>All Files URL:</strong>
                        </Typography>
                        <Box
                          sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Typography
                            className="typo-text body3_size"
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '12px',
                              wordBreak: 'break-all',
                            }}
                          >
                            {linksData.allFilesURL}
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              handleCopyLink(linksData.allFilesURL)
                            }
                          >
                            📋 Copy
                          </Button>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Typography className="typo-text body3_size">
                          <strong>DAT File URL:</strong>
                        </Typography>
                        <Box
                          sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Typography
                            className="typo-text body3_size"
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '12px',
                              wordBreak: 'break-all',
                            }}
                          >
                            {linksData.datFileURL}
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleCopyLink(linksData.datFileURL)}
                          >
                            📋 Copy
                          </Button>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>Link Password:</strong>{' '}
                        <Chip
                          label={linksData.linkPassword}
                          size="small"
                          sx={{fontFamily: 'monospace'}}
                          data-testid={`sharable-link-chip-password`}
                        />
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography className="typo-text body3_size">
                        <strong>New Expiry Date:</strong>{' '}
                        {linksData.newExpiryDate}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
            </CardContent>
          </Card>

          {/* JSON Response */}
          <Card>
            <CardContent>
              <Typography
                variant="h6"
                className="typo-text subhead3_size"
                gutterBottom
              >
                JSON Response
              </Typography>
              <Box
                sx={{
                  backgroundColor: '#f5f5f5',
                  padding: 2,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  maxHeight: 400,
                  overflow: 'auto',
                }}
              >
                <pre>{JSON.stringify(linksData, null, 2)}</pre>
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default ShareableLinksApiTest;
